import { LabeledList, TmBaseComponent } from '@treasury/presentation';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../../components/confirmation-container';
import {
    FinishedDepositPayload,
    NewDepositPayload,
    RemoteDepositEvent,
} from '../containers/remote-deposit.types';
import { DepositVm } from '../view-models';

export const tagName = 'deposit-confirmation-step';
/**
 * @fires DepositReviewStep#finished-deposit - Event fired when the user clicks the done button
 */
@customElement(tagName)
export class DepositConfirmationStep extends TmBaseComponent {
    @property({ attribute: false })
    public deposit?: DepositVm;

    protected verifiedPropNames: readonly (keyof this)[] = ['deposit'];

    protected render() {
        if (!this.deposit) {
            return nothing;
        }

        return html`<tm-section>
                <confirmation-container .headingText=${'Deposit Submitted'}>
                    <div class="p-2">
                        <div class="flex justify-between pb-2">
                            <div class="left">
                                <div class="text-xl">Deposit Total</div>
                                <div class="text-3xl font-light">
                                    ${this.deposit.formattedTotalAmount}
                                </div>
                            </div>
                            <div class="text-sm">
                                <div class="flex items-center">
                                    <tm-icon
                                        .icon=${this.deposit.statusIcon}
                                        .color=${this.deposit.statusColor}
                                    ></tm-icon>
                                    ${this.deposit.statusDescription}
                                </div>
                            </div>
                        </div>
                        <tm-labeled-list
                            class="pb-4 border-b border-dashed"
                            .list=${new LabeledList(
                                {
                                    depositDate: this.deposit.formattedDepositDate,
                                    location: this.deposit.locationName,
                                    referenceNumber: this.deposit.batchReference,
                                },
                                ['location', 'depositDate', 'referenceNumber']
                            )}
                        ></tm-labeled-list>
                        <div class="py-4">
                            <h4 class="font-medium pb-2">
                                Checks <tm-badge>${this.deposit.totalCount}</tm-badge>
                            </h4>
                            ${this.renderChecks()}
                        </div>
                    </div>
                </confirmation-container>
            </tm-section>
            <tm-footer
                .buttonConfig=${[
                    {
                        importance: 'secondary',
                        text: 'Make another deposit',
                        onClick: () => this.newDeposit(),
                    },
                    {
                        importance: 'secondary',
                        text: 'View deposit activity',
                        onClick: () => this.done(),
                    },
                ]}
            >
            </tm-footer>`;
    }

    private done() {
        this.dispatchEvent(
            new CustomEvent<FinishedDepositPayload>(RemoteDepositEvent.FinishedDeposit)
        );
    }

    private newDeposit() {
        this.dispatchEvent(new CustomEvent<NewDepositPayload>(RemoteDepositEvent.NewDeposit));
    }

    private renderChecks() {
        return this.deposit?.checks?.map(
            check =>
                html` <div class="flex justify-between w-full py-2 text-sm">
                    <div class="flex items-center text-left mr-3">
                        <div>
                            <div class="text-medium"></div>
                            <div class="text-xs" style="color: 'orange'">Pending</div>
                        </div>
                    </div>
                    <div class="flex justify-end text-right">
                        <span class="font-medium">
                            ${check.amount.toLocaleString('en-US', {
                                style: 'currency',
                                currency: 'USD',
                            })}
                        </span>
                    </div>
                </div>`
        );
    }
}
declare global {
    interface HTMLElementTagNameMap {
        [tagName]: DepositConfirmationStep;
    }
}
